import React, { createContext, useContext, useState, useEffect } from 'react';
import axiosInstance, { setAccessToken, clearAccessToken } from '../api/axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Generate random guest username
const generateGuestUsername = () => {
  const adjectives = ['Swift', 'Lucky', 'Bold', 'Wild', 'Epic', 'Stellar', 'Cosmic', 'Turbo'];
  const nouns = ['Pilot', 'Racer', 'Ace', 'Hawk', 'Phoenix', 'Comet', 'Star', 'Flyer'];
  const adj = adjectives[Math.floor(Math.random() * adjectives.length)];
  const noun = nouns[Math.floor(Math.random() * nouns.length)];
  const num = Math.floor(Math.random() * 9999);
  return `${adj}${noun}${num}`;
};

// Generate random password (user never sees it)
const generateRandomPassword = () => {
  return Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
};

export const AuthProvider = ({ children }) => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Auto-register guest function
  const autoRegisterGuest = async () => {
    try {
      const username = generateGuestUsername();
      const password = generateRandomPassword();

      const response = await axiosInstance.post('/register', {
        username,
        password
      });

      if (response.data.success) {
        setAccessToken(response.data.accessToken);
        setUserData(response.data.user);
        console.log('Auto-registered as:', username);
      }
    } catch (error) {
      console.error('Auto-registration failed:', error);
      // Retry after delay if it fails
      setTimeout(autoRegisterGuest, 2000);
    }
  };

  // Try to restore session or auto-register on mount
  useEffect(() => {
    const initializeUser = async () => {
      try {
        // Try to restore existing session via refresh token
        const response = await axiosInstance.post('/refresh_token');
        if (response.data.success && response.data.accessToken) {
          setAccessToken(response.data.accessToken);
          setUserData(response.data.user);
          console.log('Session restored:', response.data.user.username);
        }
      } catch (error) {
        // No valid session - auto-register as guest
        console.log('No active session, auto-registering guest...');
        await autoRegisterGuest();
      } finally {
        setLoading(false);
      }
    };

    initializeUser();
  }, []);

  const login = async (username, password) => {
    // Keep for potential future use, but won't be exposed in UI
    try {
      const response = await axiosInstance.post('/login', {
        username,
        password
      });

      if (response.data.success) {
        setAccessToken(response.data.accessToken);
        setUserData(response.data.user);
        return { success: true, message: response.data.message };
      } else {
        return { success: false, error: response.data.error || 'Login failed' };
      }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed'
      };
    }
  };

  const register = async (username, password) => {
    // Keep for potential future use, but won't be exposed in UI
    try {
      const response = await axiosInstance.post('/register', {
        username,
        password
      });

      if (response.data.success) {
        setAccessToken(response.data.accessToken);
        setUserData(response.data.user);
        return { success: true, message: response.data.message };
      } else {
        return { success: false, error: response.data.error || 'Registration failed' };
      }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Registration failed'
      };
    }
  };

  const logout = async () => {
    try {
      await axiosInstance.post('/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearAccessToken();
      setUserData(null);
      // Auto-register new guest after logout
      await autoRegisterGuest();
    }
  };

  const fetchUser = async () => {
    try {
      const response = await axiosInstance.get('/user');
      if (response.data) {
        setUserData(response.data);
      }
    } catch (error) {
      console.error('Fetch user error:', error);
    }
  };

  const value = {
    userData,
    setUserData,
    login,
    register,
    logout,
    fetchUser,
    isAuthenticated: !!userData,
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
